import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Users2, X, ChevronDown, ChevronRight } from 'lucide-react';

/* Vendor-based labour attendance.
   Labour is supplied by vendor/subcontractor TEAMS. For a site + day you add
   one or more teams, and under each team you add trade rows (Mason 24, Helper 30…)
   with optional overtime. The same trade can appear under multiple teams.
   Matches the real site labour report format. */

const STD_TRADES = [
    'Mason', 'Helper / General Labour', 'Carpenter', 'Bar Bender / Steel Fixer', 'Electrician',
    'Plumber', 'Painter', 'Tile Mason / Tiler', 'Welder / Fabricator', 'Fitter', 'Machine Operator',
    'RCC Worker', 'Scaffolder', 'Waterproofing Worker', 'Gypsum / False Ceiling Worker',
    'Finishing Labour', 'Hoist Operator', 'Security', 'Shuttering Carpenter',
];

const localToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const uid = () => 'r' + Date.now() + Math.floor(Math.random() * 1e6);

export default function LabourVendorEntry({ projects, api, showToast, user, isSuper, lockSite = false, fixedSite = null }) {
    const [site, setSite] = useState(fixedSite || '');
    const [date, setDate] = useState(localToday);
    const [vendors, setVendors] = useState([]);        // picker list
    const [teams, setTeams] = useState([]);            // [{key, vendor_id, vendor_name, rows:[{key, trade_name, count, ot_workers, ot_hours, in_time, out_time}]}]
    const [saving, setSaving] = useState(false);
    const [collapsed, setCollapsed] = useState({});

    const locked = !isSuper && date < localToday();

    useEffect(() => { if (fixedSite) { setSite(fixedSite); return; } if (!site && projects.length) setSite(projects[0].name); }, [projects, site, fixedSite]);

    // Load the vendor/subcontractor picker once.
    useEffect(() => {
        (async () => {
            try { const v = await api('labour_attendance/vendors'); setVendors(Array.isArray(v) ? v : []); }
            catch { setVendors([]); }
        })();
    }, []); // eslint-disable-line

    // Load existing rows for this site+day and group them by vendor.
    const load = async () => {
        if (!site) { setTeams([]); return; }
        try {
            const rows = await api(`labour_attendance?date=${date}&site=${encodeURIComponent(site)}`);
            const byVendor = {};
            (Array.isArray(rows) ? rows : []).forEach(r => {
                const vn = r.vendor_name || 'Unassigned';
                if (!byVendor[vn]) byVendor[vn] = { key: uid(), vendor_id: r.vendor_id || '', vendor_name: vn, rows: [] };
                byVendor[vn].rows.push({ key: uid(), trade_name: r.trade_name, count: Number(r.count) || 0, ot_workers: Number(r.ot_workers) || 0, ot_hours: Number(r.ot_hours) || 0, in_time: r.in_time || '', out_time: r.out_time || '' });
            });
            setTeams(Object.values(byVendor));
        } catch { setTeams([]); }
    };
    useEffect(() => { load(); }, [site, date]); // eslint-disable-line

    const total = teams.reduce((s, t) => s + t.rows.reduce((a, r) => a + (Number(r.count) || 0), 0), 0);
    const otTotal = teams.reduce((s, t) => s + t.rows.reduce((a, r) => a + (Number(r.ot_workers) || 0), 0), 0);

    // --- team + row editing (local) ---
    const addTeam = () => setTeams(t => [...t, { key: uid(), vendor_id: '', vendor_name: '', rows: [{ key: uid(), trade_name: '', count: 0, ot_workers: 0, ot_hours: 0, in_time: '', out_time: '' }] }]);
    const removeTeam = (key) => setTeams(t => t.filter(x => x.key !== key));
    const setTeamVendor = (key, val) => {
        const v = vendors.find(x => x.name === val);
        setTeams(t => t.map(x => x.key === key ? { ...x, vendor_name: val, vendor_id: v?.id || '' } : x));
    };
    const addRow = (teamKey) => setTeams(t => t.map(x => x.key === teamKey ? { ...x, rows: [...x.rows, { key: uid(), trade_name: '', count: 0, ot_workers: 0, ot_hours: 0, in_time: '', out_time: '' }] } : x));
    const removeRow = (teamKey, rowKey) => setTeams(t => t.map(x => x.key === teamKey ? { ...x, rows: x.rows.filter(r => r.key !== rowKey) } : x));
    const setRow = (teamKey, rowKey, patch) => setTeams(t => t.map(x => x.key === teamKey ? { ...x, rows: x.rows.map(r => r.key === rowKey ? { ...r, ...patch } : r) } : x));
    const toggle = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

    const save = async () => {
        if (!site) { showToast?.('Pick a site first.'); return; }
        setSaving(true);
        try {
            const entries = [];
            teams.forEach(t => t.rows.forEach(r => {
                if (!r.trade_name) return;
                entries.push({
                    vendor_id: t.vendor_id || '', vendor_name: t.vendor_name || '',
                    trade_name: r.trade_name, count: Number(r.count) || 0,
                    ot_workers: Number(r.ot_workers) || 0, ot_hours: Number(r.ot_hours) || 0,
                    in_time: r.in_time || '', out_time: r.out_time || '',
                });
            }));
            const res = await api('labour_attendance', 'POST', { date, site, marked_by: user?.full_name || user?.name || '', entries });
            showToast?.(`Saved — ${res?.total ?? total} workers at ${site} on ${date}.`);
            await load();
        } catch (e) {
            showToast?.(e?.message?.includes('locked') ? 'This day is locked — Super Admin only.' : 'Error saving labour.');
        } finally { setSaving(false); }
    };

    const teamTotal = (t) => t.rows.reduce((a, r) => a + (Number(r.count) || 0), 0);

    return (
        <div>
            <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: '16px' }}>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '4px' }}>SITE</label>
                    {lockSite ? (
                        <div style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: 700, minWidth: '220px', background: '#f8fafc' }}>{site || '—'}</div>
                    ) : (
                        <select value={site} onChange={e => setSite(e.target.value)} style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px', fontWeight: 600, minWidth: '220px' }}>
                            {projects.map(p => <option key={p.id} value={p.name}>{p.name}</option>)}
                        </select>
                    )}
                </div>
                <div>
                    <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '4px' }}>DATE</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)} style={{ padding: '9px 12px', border: '1px solid #e2e8f0', borderRadius: '8px' }} />
                </div>
                <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                    <div style={{ fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Total Present</div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#1743e6', lineHeight: 1 }}>{total}</div>
                    {otTotal > 0 && <div style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 700 }}>+{otTotal} on OT</div>}
                </div>
            </div>

            {locked && (
                <div style={{ padding: '10px 14px', marginBottom: '14px', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '10px', fontSize: '13px', color: '#92400e', fontWeight: 600 }}>
                    🔒 This day is locked. Only a Super Admin can change past labour attendance.
                </div>
            )}

            {teams.map(t => (
                <div key={t.key} style={{ border: '1px solid #e2e8f0', borderRadius: '12px', marginBottom: '14px', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 14px', background: '#f8fafc', borderBottom: collapsed[t.key] ? 'none' : '1px solid #eef2f7' }}>
                        <button onClick={() => toggle(t.key)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: 0 }}>
                            {collapsed[t.key] ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                        </button>
                        <Users2 size={16} color="#1743e6" />
                        <select value={t.vendor_name} disabled={locked} onChange={e => setTeamVendor(t.key, e.target.value)}
                            style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '7px', fontWeight: 700, minWidth: '220px' }}>
                            <option value="">— Select team / vendor —</option>
                            {vendors.map(v => <option key={v.id} value={v.name}>{v.name}</option>)}
                            {t.vendor_name && !vendors.some(v => v.name === t.vendor_name) && <option value={t.vendor_name}>{t.vendor_name}</option>}
                        </select>
                        <span style={{ fontSize: '12px', color: '#64748b' }}>· {teamTotal(t)} workers</span>
                        {!locked && <button onClick={() => removeTeam(t.key)} style={{ marginLeft: 'auto', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', borderRadius: '7px', padding: '5px 8px', cursor: 'pointer' }}><Trash2 size={13} /></button>}
                    </div>

                    {!collapsed[t.key] && (
                        <div style={{ padding: '10px 14px' }}>
                            {t.rows.map(r => (
                                <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 0', flexWrap: 'wrap' }}>
                                    <input list="std-trades" value={r.trade_name} disabled={locked} placeholder="Trade (e.g. Mason)"
                                        onChange={e => setRow(t.key, r.key, { trade_name: e.target.value })}
                                        style={{ flex: 1, minWidth: '180px', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '7px', fontWeight: 600 }} />
                                    <input type="number" min="0" value={r.count} disabled={locked} title="Count"
                                        onChange={e => setRow(t.key, r.key, { count: Math.max(0, parseInt(e.target.value) || 0) })}
                                        style={{ width: '70px', textAlign: 'center', padding: '7px', border: '1px solid #e2e8f0', borderRadius: '7px', fontWeight: 700 }} />
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#f59e0b', textTransform: 'uppercase' }}>OT</span>
                                    <input type="number" min="0" value={r.ot_workers} disabled={locked} title="OT workers" placeholder="0"
                                        onChange={e => setRow(t.key, r.key, { ot_workers: Math.max(0, parseInt(e.target.value) || 0) })}
                                        style={{ width: '48px', textAlign: 'center', padding: '7px', border: '1px solid #fde68a', borderRadius: '7px', fontSize: '12px', background: '#fffbeb' }} />
                                    <input type="number" min="0" step="0.5" value={r.ot_hours} disabled={locked} title="OT hours" placeholder="0"
                                        onChange={e => setRow(t.key, r.key, { ot_hours: Math.max(0, parseFloat(e.target.value) || 0) })}
                                        style={{ width: '54px', textAlign: 'center', padding: '7px', border: '1px solid #fde68a', borderRadius: '7px', fontSize: '12px', background: '#fffbeb' }} />
                                    <span style={{ fontSize: '10px', color: '#94a3b8' }}>hrs</span>
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#0ea5e9', textTransform: 'uppercase', marginLeft: '4px' }}>In</span>
                                    <input type="time" value={r.in_time || ''} disabled={locked} title="In time"
                                        onChange={e => setRow(t.key, r.key, { in_time: e.target.value })}
                                        style={{ padding: '6px', border: '1px solid #bae6fd', borderRadius: '7px', fontSize: '12px', background: '#f0f9ff' }} />
                                    <span style={{ fontSize: '10px', fontWeight: 700, color: '#0ea5e9', textTransform: 'uppercase' }}>Out</span>
                                    <input type="time" value={r.out_time || ''} disabled={locked} title="Out time"
                                        onChange={e => setRow(t.key, r.key, { out_time: e.target.value })}
                                        style={{ padding: '6px', border: '1px solid #bae6fd', borderRadius: '7px', fontSize: '12px', background: '#f0f9ff' }} />
                                    {!locked && <button onClick={() => removeRow(t.key, r.key)} style={{ border: 'none', background: 'none', color: '#cbd5e1', cursor: 'pointer' }}><X size={15} /></button>}
                                </div>
                            ))}
                            {!locked && (
                                <button onClick={() => addRow(t.key)} style={{ marginTop: '6px', border: '1px dashed #cbd5e1', background: '#fff', color: '#1743e6', borderRadius: '7px', padding: '6px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600, display: 'inline-flex', gap: '5px', alignItems: 'center' }}>
                                    <Plus size={13} /> Add trade
                                </button>
                            )}
                        </div>
                    )}
                </div>
            ))}

            <datalist id="std-trades">{STD_TRADES.map(t => <option key={t} value={t} />)}</datalist>

            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginTop: '8px' }}>
                {!locked && (
                    <button onClick={addTeam} style={{ border: '1px solid #1743e6', background: '#fff', color: '#1743e6', borderRadius: '8px', padding: '10px 16px', cursor: 'pointer', fontWeight: 700, display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        <Plus size={15} /> Add Team / Vendor
                    </button>
                )}
                <button onClick={save} disabled={saving || locked} style={{ marginLeft: 'auto', minWidth: '190px', border: 'none', background: locked ? '#94a3b8' : '#1743e6', color: '#fff', borderRadius: '8px', padding: '11px 18px', cursor: locked ? 'not-allowed' : 'pointer', fontWeight: 700 }}>
                    {locked ? 'Locked' : saving ? 'Saving…' : 'Save Labour Attendance'}
                </button>
            </div>

            {teams.length === 0 && !locked && (
                <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8', fontSize: '13px' }}>No teams added yet. Click "Add Team / Vendor" to log labour by team.</div>
            )}
        </div>
    );
}
